import { useState, useMemo, useEffect, useCallback } from "react";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import {
  ShieldCheck, Plus, Pencil, Trash2, Search, X,
  Server, Cloud, HardDrive, CheckCircle2,
  XCircle, ChevronRight, Info, Layers, Check,
  ChevronLeft, Loader2,
} from "lucide-react";
import { myLocalStorage } from "../../StorageHelper";
import { useToast } from "../../../hooks/use-toast";
import {
  createMultiApprovalGroup,
  createMultiApprovalPolicy,
  cancelActiveRequestsForGroup,
  cancelActiveRequestsForPolicy,
  deleteMultiApprovalGroup,
  deleteMultiApprovalPolicy,
  fetchMultiApprovalAdmins,
  fetchMultiApprovalGroups,
  fetchMultiApprovalPolicies,
  fetchMultiApprovalResources,
  MultiApprovalAdmin,
  MultiApprovalGroup as ApiMultiApprovalGroup,
  MultiApprovalPolicy as ApiMultiApprovalPolicy,
  MultiApprovalResourceCategory,
  MultiApprovalResourceItem,
  updateMultiApprovalGroup,
  updateMultiApprovalPolicy,
} from "./Api/multiapprovalapi";
import { getAvatarColor, getInitials } from "./helpers";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../ui/tooltip";

// ─── Types ────────────────────────────────────────────────────────────────────

type PolicyStatus = "active" | "inactive";

interface GroupMember {
  id: string; name: string; email: string; role: string; initials: string; avatarColor: string;
  firstName?: string; lastName?: string;
}

interface ApprovalGroup {
  id: string; name: string; description: string; members: GroupMember[];
  systemDefault?: boolean; hasActivePendingRequests?: boolean; editable?: boolean;
}

interface PolicyResource {
  resourceKey: string;
  resourceSource: string;
  resourceSourceLabel: string;
  resourceType: string;
  resourceTypeLabel: string;
  resourceId: string;
  name: string;
  detail?: string;
}

interface ApprovalRule {
  groupId: string; minApprovals: number;
}

interface ApprovalPolicy {
  id: string; name: string; description: string; status: PolicyStatus;
  resources: PolicyResource[]; rules: ApprovalRule[]; createdAt: string;
  hasActivePendingRequests?: boolean; editable?: boolean;
}

// ─── Utilities ────────────────────────────────────────────────────────────────

const LIST_PAGE_SIZE = 15;
const SELECTOR_FETCH_SIZE = 500;
const DESCRIPTION_MAX_LENGTH = 120;
const RESOURCE_FETCH_LIMIT = 100;

function maxMinApprovalsForGroup(groups: ApprovalGroup[], groupId: string) {
  const group = groups.find((g) => g.id === groupId);
  return Math.max(1, group?.members.length ?? 1);
}

function minApprovalOptions(groups: ApprovalGroup[], groupId: string) {
  const max = maxMinApprovalsForGroup(groups, groupId);
  return Array.from({ length: max }, (_, index) => index + 1);
}

function RequiredMark() {
  return <span className="text-destructive ml-0.5">*</span>;
}

function ruleUsesDuplicateGroup(rules: ApprovalRule[]) {
  const seen = new Set<string>();
  for (const rule of rules) {
    if (!rule.groupId) continue;
    if (seen.has(rule.groupId)) return true;
    seen.add(rule.groupId);
  }
  return false;
}

function firstAvailableGroupId(groups: ApprovalGroup[], rules: ApprovalRule[]) {
  const usedGroupIds = new Set(rules.map((rule) => rule.groupId).filter(Boolean));
  return groups.find((group) => !usedGroupIds.has(group.id))?.id ?? "";
}

function buildResourceIdentity(source: string, type: string, id: string) {
  return `${normalizeResourcePart(source)}|${normalizeResourcePart(type)}|${String(id || "").trim()}`;
}

function resourceIdentity(resource: Pick<PolicyResource, "resourceSource" | "resourceType" | "resourceId">) {
  return buildResourceIdentity(resource.resourceSource, resource.resourceType, resource.resourceId);
}

function normalizeResourceKey(key?: string) {
  if (!key) return "";
  const parts = key.split("|");
  if (parts.length !== 3) return key.trim();
  return buildResourceIdentity(parts[0], parts[1], parts[2]);
}

function resourcesMatch(
  a: Pick<PolicyResource, "resourceKey" | "resourceSource" | "resourceType" | "resourceId" | "name">,
  b: Pick<PolicyResource, "resourceKey" | "resourceSource" | "resourceType" | "resourceId" | "name">,
) {
  const aKey = normalizeResourceKey(a.resourceKey || resourceIdentity(a));
  const bKey = normalizeResourceKey(b.resourceKey || resourceIdentity(b));
  if (aKey && bKey && aKey === bKey) {
    return true;
  }
  if (normalizeResourcePart(a.resourceSource) === normalizeResourcePart(b.resourceSource)
    && normalizeResourcePart(a.resourceType) === normalizeResourcePart(b.resourceType)) {
    if (String(a.resourceId || "") === String(b.resourceId || "")) {
      return true;
    }
    const aName = (a.name || "").trim();
    const bName = (b.name || "").trim();
    if (aName && bName && aName.toLowerCase() === bName.toLowerCase()) {
      return true;
    }
    if (aName && String(b.resourceId || "").toLowerCase() === aName.toLowerCase()) {
      return true;
    }
    if (bName && String(a.resourceId || "").toLowerCase() === bName.toLowerCase()) {
      return true;
    }
  }
  return false;
}

function normalizeResourcePart(value?: string) {
  return (value || "").trim().toUpperCase();
}

function reconcilePolicyResources(
  selected: PolicyResource[],
  categories: MultiApprovalResourceCategory[],
): PolicyResource[] {
  const catalogItems = categories.flatMap((category) =>
    category.resources.map((item) => resourceItemToPolicyResource(item)),
  );
  return selected.map((sel) => catalogItems.find((item) => resourcesMatch(sel, item)) ?? sel);
}

const SOURCE_STYLES: Record<string, { label: string; bg: string; text: string; border: string }> = {
  ENDPOINT: { label: "Endpoints", bg: "bg-slate-50 dark:bg-slate-950/40", text: "text-slate-700 dark:text-slate-300", border: "border-slate-200 dark:border-slate-800" },
  AWS: { label: "AWS", bg: "bg-amber-50 dark:bg-amber-950/40", text: "text-amber-700 dark:text-amber-300", border: "border-amber-200 dark:border-amber-800" },
  AZURE: { label: "Azure", bg: "bg-sky-50 dark:bg-sky-950/40", text: "text-sky-700 dark:text-sky-300", border: "border-sky-200 dark:border-sky-800" },
  GCP: { label: "GCP", bg: "bg-blue-50 dark:bg-blue-950/40", text: "text-blue-700 dark:text-blue-300", border: "border-blue-200 dark:border-blue-800" },
  K8: { label: "Kubernetes", bg: "bg-violet-50 dark:bg-violet-950/40", text: "text-violet-700 dark:text-violet-300", border: "border-violet-200 dark:border-violet-800" },
};

function SourceBadge({ source, label }: { source: string; label?: string }) {
  const style = SOURCE_STYLES[source] ?? SOURCE_STYLES.ENDPOINT;
  return (
    <Badge variant="outline" className={`text-[10px] py-0 font-semibold ${style.bg} ${style.text} ${style.border}`}>
      {label ?? style.label}
    </Badge>
  );
}

function ResourceSourceIcon({ source, className = "w-3.5 h-3.5" }: { source: string; className?: string }) {
  if (source === "ENDPOINT") return <Server className={`${className} text-slate-500 dark:text-slate-400`} />;
  if (source === "AWS") return <Cloud className={`${className} text-amber-500 dark:text-amber-400`} />;
  if (source === "AZURE") return <Cloud className={`${className} text-sky-500 dark:text-sky-400`} />;
  if (source === "GCP") return <Cloud className={`${className} text-blue-500 dark:text-blue-400`} />;
  if (source === "K8") return <Cloud className={`${className} text-violet-500 dark:text-violet-400`} />;
  return <HardDrive className={`${className} text-muted-foreground`} />;
}

function MemberAvatars({ members, max = 4 }: { members: GroupMember[]; max?: number }) {
  const visible = members.slice(0, max);
  const rest = members.length - max;
  return (
    <div className="flex items-center">
      {visible.map((m, i) => (
        <div
          key={m.id}
          className={`w-6 h-6 rounded-full ${m.avatarColor} flex items-center justify-center text-white text-[9px] font-bold border-2 border-background ${i > 0 ? "-ml-1.5" : ""}`}
          title={`${m.name} (${m.role})`}
        >
          {m.initials}
        </div>
      ))}
      {rest > 0 && (
        <div className="-ml-1.5 w-6 h-6 rounded-full bg-muted border-2 border-background flex items-center justify-center text-[9px] font-semibold text-muted-foreground">
          +{rest}
        </div>
      )}
    </div>
  );
}

function Pagination({ page, total, pageSize, onChange }: {
  page: number; total: number; pageSize: number; onChange: (p: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between border-t border-border pt-3 mt-2">
      <span className="text-xs text-muted-foreground">
        Showing {Math.min((page - 1) * pageSize + 1, total)}–{Math.min(page * pageSize, total)} of {total}
      </span>
      <div className="flex items-center gap-1">
        <Button size="icon" variant="ghost" disabled={page <= 1} onClick={() => onChange(page - 1)}>
          <ChevronLeft className="w-3.5 h-3.5" />
        </Button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
          <Button
            key={p}
            size="sm"
            variant={p === page ? "default" : "ghost"}
            onClick={() => onChange(p)}
            className="w-8"
          >
            {p}
          </Button>
        ))}
        <Button size="icon" variant="ghost" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
          <ChevronRight className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ─── Resource list used in both dialog + expanded view ────────────────────────

function ResourceListSections({ resources }: { resources: PolicyResource[] }) {
  const grouped = useMemo(() => {
    const map = new Map<string, { sourceLabel: string; types: Map<string, { typeLabel: string; items: PolicyResource[] }> }>();
    for (const resource of resources) {
      if (!map.has(resource.resourceSource)) {
        map.set(resource.resourceSource, {
          sourceLabel: resource.resourceSourceLabel,
          types: new Map(),
        });
      }
      const sourceGroup = map.get(resource.resourceSource)!;
      if (!sourceGroup.types.has(resource.resourceType)) {
        sourceGroup.types.set(resource.resourceType, {
          typeLabel: resource.resourceTypeLabel,
          items: [],
        });
      }
      sourceGroup.types.get(resource.resourceType)!.items.push(resource);
    }
    return Array.from(map.entries());
  }, [resources]);

  if (resources.length === 0) {
    return <p className="text-xs text-muted-foreground">No resources selected.</p>;
  }

  return (
    <div className="space-y-3">
      {grouped.map(([source, sourceGroup]) => (
        <div key={source}>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
            {sourceGroup.sourceLabel}
          </p>
          <div className="space-y-2">
            {Array.from(sourceGroup.types.entries()).map(([type, typeGroup]) => (
              <div key={`${source}-${type}`}>
                <p className="text-[10px] font-medium text-muted-foreground px-2 mb-1">{typeGroup.typeLabel}</p>
                <div className="space-y-1">
                  {typeGroup.items.map((res) => (
                    <div key={res.resourceKey} className="flex items-center gap-2 px-2">
                      <ResourceSourceIcon source={res.resourceSource} />
                      <span className="text-xs font-medium text-foreground flex-1">{res.name}</span>
                      <SourceBadge source={res.resourceSource} label={res.resourceSourceLabel} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function resourceItemToPolicyResource(item: MultiApprovalResourceItem): PolicyResource {
  return {
    resourceKey: item.resourceKey,
    resourceSource: item.resourceSource,
    resourceSourceLabel: item.resourceSourceLabel,
    resourceType: item.resourceType,
    resourceTypeLabel: item.resourceTypeLabel,
    resourceId: item.resourceId,
    name: item.name,
    detail: item.detail,
  };
}

function apiPolicyToApprovalPolicy(policy: ApiMultiApprovalPolicy): ApprovalPolicy {
  return {
    id: policy.id,
    name: policy.name,
    description: policy.description,
    status: policy.status,
    hasActivePendingRequests: policy.hasActivePendingRequests,
    editable: policy.editable,
    createdAt: policy.createdAt?.slice(0, 10) ?? "",
    resources: policy.resources.map((resource) => ({
      resourceKey: resource.resourceKey,
      resourceSource: resource.resourceSource,
      resourceSourceLabel: resource.resourceSourceLabel,
      resourceType: resource.resourceType,
      resourceTypeLabel: resource.resourceTypeLabel,
      resourceId: resource.resourceId,
      name: resource.resourceName,
    })),
    rules: policy.rules.map((rule) => ({
      groupId: rule.groupUuid,
      minApprovals: rule.minApprovals,
    })),
  };
}

// ─── Group Dialog ─────────────────────────────────────────────────────────────

function adminToGroupMember(admin: MultiApprovalAdmin): GroupMember {
  const name = admin.fullName || `${admin.firstName} ${admin.lastName}`.trim() || admin.email;
  return {
    id: String(admin.id),
    name,
    email: admin.email,
    role: admin.roleName,
    firstName: admin.firstName,
    lastName: admin.lastName,
    initials: getInitials(name, admin.email),
    avatarColor: getAvatarColor(admin.id),
  };
}

function apiGroupToApprovalGroup(group: ApiMultiApprovalGroup): ApprovalGroup {
  return {
    id: group.id,
    name: group.name,
    description: group.description,
    systemDefault: group.systemDefault,
    hasActivePendingRequests: group.hasActivePendingRequests,
    editable: group.editable,
    members: group.members.map((member) => {
      const name = member.fullName || `${member.firstName} ${member.lastName}`.trim() || member.email;
      return {
        id: String(member.tenantAdminId),
        name,
        email: member.email,
        role: member.roleName,
        firstName: member.firstName,
        lastName: member.lastName,
        initials: getInitials(name, member.email),
        avatarColor: getAvatarColor(member.tenantAdminId),
      };
    }),
  };
}

function GroupDialog({
  open, onOpenChange, initial, onSave, tenantName,
}: {
  open: boolean; onOpenChange: (v: boolean) => void;
  initial: Partial<ApprovalGroup> | null;
  onSave: (g: Omit<ApprovalGroup, "id"> & { id?: string }) => void;
  tenantName: string;
}) {
  const isEdit = !!initial?.id;
  const isReadOnly = isEdit && initial?.editable === false;
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [members, setMembers] = useState<GroupMember[]>(initial?.members ?? []);
  const [memberSearch, setMemberSearch] = useState("");
  const [availableAdmins, setAvailableAdmins] = useState<MultiApprovalAdmin[]>([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [activeSearch, setActiveSearch] = useState("");

  const loadAdmins = useCallback(async (search = "") => {
    if (!tenantName) return;
    setAdminsLoading(true);
    try {
      const response = await fetchMultiApprovalAdmins(
        tenantName,
        0,
        10,
        search,
      );
      setAvailableAdmins(response.content ?? []);
      setActiveSearch(search);
    } catch (error) {
      console.error("Failed to load admins:", error);
      setAvailableAdmins([]);
    } finally {
      setAdminsLoading(false);
    }
  }, [tenantName]);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? "");
    setDescription(initial?.description ?? "");
    setMembers(initial?.members ?? []);
    setMemberSearch("");
    setActiveSearch("");
    loadAdmins();
  }, [open, initial, loadAdmins]);

  const handleAdminSearch = () => {
    loadAdmins(memberSearch.trim());
  };

  const toggle = (admin: MultiApprovalAdmin) => {
    if (isReadOnly) return;
    const member = adminToGroupMember(admin);
    setMembers((prev) =>
      prev.find((m) => m.id === member.id)
        ? prev.filter((m) => m.id !== member.id)
        : [...prev, member],
    );
  };

  const handleSave = () => {
    if (!name.trim() || isReadOnly) return;
    onSave({ id: initial?.id, name: name.trim(), description: description.trim(), members });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Approval Group" : "Create Approval Group"}</DialogTitle>
        </DialogHeader>
        {isReadOnly && (
          <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
            This group has active pending approval requests. Resolve those requests before editing.
          </div>
        )}
        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">
              Group Name
              <RequiredMark />
            </label>
            <Input placeholder="e.g. Production Team" value={name} onChange={(e) => setName(e.target.value)} disabled={isReadOnly} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Description</label>
            <Input
              placeholder="Short description of this group's responsibility"
              value={description}
              maxLength={DESCRIPTION_MAX_LENGTH}
              onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_MAX_LENGTH))}
              disabled={isReadOnly}
            />
            <p className="text-[10px] text-muted-foreground text-right">{description.length}/{DESCRIPTION_MAX_LENGTH}</p>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">
              Members ({members.length} selected)
              <RequiredMark />
            </label>
            <div className="flex gap-2 mb-1.5">
              <div className="relative flex-1">
                <Input
                  className="h-8 text-xs"
                  placeholder="Search admins by name, email, or role…"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleAdminSearch(); }}
                />
              </div>
              <Button
                size="icon"
                variant="outline"
                className="h-8 w-8 flex-shrink-0"
                onClick={handleAdminSearch}
                disabled={adminsLoading}
              >
                {adminsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
              </Button>
            </div>
            <div className="border border-border rounded-md divide-y divide-border max-h-44 overflow-y-auto">
              {adminsLoading && availableAdmins.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">Loading admins…</p>
              ) : availableAdmins.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">
                  {activeSearch ? "No admins match your search." : "No admins found."}
                </p>
              ) : (
                availableAdmins.map((admin) => {
                  const selected = !!members.find((m) => m.id === String(admin.id));
                  const displayName = admin.fullName || `${admin.firstName} ${admin.lastName}`.trim() || admin.email;
                  const initials = getInitials(displayName, admin.email);
                  const avatarColor = getAvatarColor(admin.id);
                  return (
                    <button
                      key={admin.id}
                      type="button"
                      disabled={isReadOnly}
                      onClick={() => toggle(admin)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover-elevate text-left disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <div className={`w-7 h-7 rounded-full ${avatarColor} flex items-center justify-center text-white text-xs font-bold shrink-0`}>
                        {initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">{displayName}</p>
                        <p className="text-xs text-muted-foreground truncate">{admin.email}</p>
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0">{admin.roleName}</span>
                      <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${selected ? "bg-primary border-primary" : "border-border"}`}>
                        {selected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={!name.trim() || members.length === 0 || isReadOnly}>
            {isEdit ? "Save Changes" : "Create Group"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Policy Dialog ────────────────────────────────────────────────────────────

function PolicyDialog({
  open, onOpenChange, initial, groups, allPolicies, onSave, tenantName,
}: {
  open: boolean; onOpenChange: (v: boolean) => void;
  initial: Partial<ApprovalPolicy> | null;
  groups: ApprovalGroup[];
  allPolicies: ApprovalPolicy[];
  onSave: (p: Omit<ApprovalPolicy, "id" | "createdAt"> & { id?: string }) => void;
  tenantName: string;
}) {
  const isEdit = !!initial?.id;
  const isReadOnly = isEdit && initial?.editable === false;
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [status, setStatus] = useState<PolicyStatus>(initial?.status ?? "active");
  const [selectedResources, setSelectedResources] = useState<PolicyResource[]>(initial?.resources ?? []);
  const [rules, setRules] = useState<ApprovalRule[]>(initial?.rules ?? [{ groupId: groups[0]?.id ?? "", minApprovals: 1 }]);
  const [resourceSearch, setResourceSearch] = useState("");
  const [resourceCategories, setResourceCategories] = useState<MultiApprovalResourceCategory[]>([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [activeResourceSearch, setActiveResourceSearch] = useState("");
  const [tab, setTab] = useState<"resources" | "rules">("resources");
  const [didReconcileResources, setDidReconcileResources] = useState(false);

  const loadResources = useCallback(async (search = "") => {
    if (!tenantName) return;
    setResourcesLoading(true);
    try {
      const response = await fetchMultiApprovalResources(
        tenantName,
        RESOURCE_FETCH_LIMIT,
        search,
        initial?.id,
      );
      setResourceCategories(response.categories ?? []);
      setActiveResourceSearch(search);
    } catch (error) {
      console.error("Failed to load resources:", error);
      setResourceCategories([]);
    } finally {
      setResourcesLoading(false);
    }
  }, [tenantName, initial?.id]);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? "");
    setDescription(initial?.description ?? "");
    setStatus(initial?.status ?? "active");
    setSelectedResources(initial?.resources ?? []);
    setRules(initial?.rules ?? [{ groupId: groups[0]?.id ?? "", minApprovals: 1 }]);
    setResourceSearch("");
    setActiveResourceSearch("");
    setDidReconcileResources(false);
  }, [open, initial, groups]);

  useEffect(() => {
    if (!open) return;
    loadResources();
  }, [open, initial?.id, allPolicies, loadResources]);

  useEffect(() => {
    if (!open || didReconcileResources || resourceCategories.length === 0 || !initial?.resources?.length) {
      return;
    }
    setSelectedResources(reconcilePolicyResources(initial.resources, resourceCategories));
    setDidReconcileResources(true);
  }, [open, didReconcileResources, resourceCategories, initial?.resources]);

  const isResourceSelected = (item: MultiApprovalResourceItem | PolicyResource) =>
    selectedResources.some((existing) => resourcesMatch(existing, item));

  const resourcesAssignedElsewhere = useMemo(() => {
    const assigned: PolicyResource[] = [];
    for (const policy of allPolicies) {
      if (policy.id && policy.id === initial?.id) continue;
      assigned.push(...policy.resources);
    }
    return assigned;
  }, [allPolicies, initial?.id]);

  const isResourceAvailable = (item: MultiApprovalResourceItem) =>
    !resourcesAssignedElsewhere.some((resource) => resourcesMatch(resource, item));

  const availableResourceCategories = useMemo(
    () => resourceCategories
      .map((category) => ({
        ...category,
        resources: category.resources.filter(isResourceAvailable),
      }))
      .filter((category) => category.resources.length > 0),
    [resourceCategories, resourcesAssignedElsewhere],
  );

  const toggleResource = (item: MultiApprovalResourceItem) => {
    if (isReadOnly) return;
    const resource = resourceItemToPolicyResource(item);
    setSelectedResources((prev) =>
      prev.some((existing) => resourcesMatch(existing, resource))
        ? prev.filter((existing) => !resourcesMatch(existing, resource))
        : [...prev, resource],
    );
  };

  const removeSelectedResource = (resource: PolicyResource) => {
    if (isReadOnly) return;
    setSelectedResources((prev) => prev.filter((existing) => !resourcesMatch(existing, resource)));
  };

  const addRule = () => {
    const nextGroupId = firstAvailableGroupId(groups, rules);
    if (!nextGroupId) return;
    setRules((prev) => [...prev, { groupId: nextGroupId, minApprovals: 1 }]);
  };
  const removeRule = (i: number) => setRules((prev) => prev.filter((_, idx) => idx !== i));
  const updateRule = (i: number, patch: Partial<ApprovalRule>) =>
    setRules((prev) => prev.map((r, idx) => {
      if (idx !== i) return r;
      if (patch.groupId && prev.some((rule, ruleIdx) => ruleIdx !== i && rule.groupId === patch.groupId)) {
        return r;
      }
      const next = { ...r, ...patch };
      if (patch.groupId) {
        const max = maxMinApprovalsForGroup(groups, patch.groupId);
        if (next.minApprovals > max) {
          next.minApprovals = max;
        }
      }
      return next;
    }));

  const canAddRule = groups.some((group) => !rules.some((rule) => rule.groupId === group.id));

  const valid = !isReadOnly && name.trim() && selectedResources.length > 0 && rules.length > 0
    && rules.every((r) => r.groupId && r.minApprovals >= 1)
    && !ruleUsesDuplicateGroup(rules);

  const handleSave = () => {
    if (!valid || isReadOnly) return;
    onSave({
      id: initial?.id,
      name: name.trim(),
      description: description.trim(),
      status,
      resources: selectedResources,
      rules,
    });
    onOpenChange(false);
  };

  const ResourceRow = ({ item }: { item: MultiApprovalResourceItem }) => {
    const selected = isResourceSelected(item);
    return (
      <button
        type="button"
        disabled={isReadOnly}
        onClick={() => toggleResource(item)}
        className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded hover-elevate text-left disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <ResourceSourceIcon source={item.resourceSource} />
        <span className="text-xs font-medium text-foreground flex-1">{item.name}</span>
        <SourceBadge source={item.resourceSource} label={item.resourceSourceLabel} />
        {item.detail && <span className="text-[10px] text-muted-foreground shrink-0">{item.detail}</span>}
        <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${selected ? "bg-primary border-primary" : "border-border"}`}>
          {selected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
        </div>
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Approval Policy" : "Create Approval Policy"}</DialogTitle>
        </DialogHeader>
        {isReadOnly && (
          <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
            This policy has active pending approval requests. Resolve those requests before editing.
          </div>
        )}
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">
                Policy Name
                <RequiredMark />
              </label>
              <Input placeholder="e.g. Production Server Access" value={name} onChange={(e) => setName(e.target.value)} disabled={isReadOnly} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">Status</label>
              <Select value={status} onValueChange={(v) => setStatus(v as PolicyStatus)} disabled={isReadOnly}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Description</label>
            <Input
              placeholder="Describe when this policy applies"
              value={description}
              maxLength={DESCRIPTION_MAX_LENGTH}
              onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_MAX_LENGTH))}
              disabled={isReadOnly}
            />
            <p className="text-[10px] text-muted-foreground text-right">{description.length}/{DESCRIPTION_MAX_LENGTH}</p>
          </div>

          <div className="border border-border rounded-md overflow-hidden">
            <div className="flex border-b border-border">
              {(["resources", "rules"] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`flex-1 py-2 text-xs font-medium transition-colors ${tab === t ? "bg-muted text-foreground" : "text-muted-foreground hover-elevate"}`}
                >
                  {t === "resources"
                    ? <>Resources ({selectedResources.length} selected)<RequiredMark /></>
                    : <>Approval Rules ({rules.length})<RequiredMark /></>}
                </button>
              ))}
            </div>

            {tab === "resources" && (
              <div className="p-3">
                {selectedResources.length > 0 && (
                  <div className="mb-3 rounded-md border border-border bg-muted/30 p-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-1 mb-1.5">
                      Selected ({selectedResources.length})
                    </p>
                    <div className="space-y-1 max-h-28 overflow-y-auto">
                      {selectedResources.map((resource) => (
                        <div key={resource.resourceKey || resourceIdentity(resource)} className="flex items-center gap-2 px-1">
                          <ResourceSourceIcon source={resource.resourceSource} />
                          <span className="text-xs font-medium text-foreground flex-1">{resource.name}</span>
                          <SourceBadge source={resource.resourceSource} label={resource.resourceSourceLabel} />
                          {!isReadOnly && (
                            <button
                              type="button"
                              onClick={() => removeSelectedResource(resource)}
                              className="text-[10px] text-muted-foreground hover:text-destructive px-1"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2 mb-3">
                  <Input
                    className="h-7 text-xs flex-1"
                    placeholder="Search resources by name…"
                    value={resourceSearch}
                    onChange={(e) => setResourceSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") loadResources(resourceSearch.trim()); }}
                  />
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-7 w-7 flex-shrink-0"
                    onClick={() => loadResources(resourceSearch.trim())}
                    disabled={resourcesLoading}
                  >
                    {resourcesLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                  </Button>
                </div>
                <div className="max-h-52 overflow-y-auto space-y-3">
                  {resourcesLoading && availableResourceCategories.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4">Loading resources…</p>
                  ) : availableResourceCategories.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4">
                      {activeResourceSearch ? "No resources match your search." : "No resources found."}
                    </p>
                  ) : (
                    availableResourceCategories.map((category) => (
                      <div key={`${category.resourceSource}-${category.resourceType}`}>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 mb-1">
                          {category.resourceSourceLabel} · {category.resourceTypeLabel}
                        </p>
                        {category.resources.map((item) => (
                          <ResourceRow key={item.resourceKey} item={item} />
                        ))}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {tab === "rules" && (
              <div className="p-3 space-y-2 max-h-72 overflow-y-auto">
                <p className="text-xs text-muted-foreground">All rules must be satisfied before access is granted (AND logic).</p>
                {rules.map((rule, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <span className="text-[10px] font-bold text-muted-foreground">{i + 1}</span>
                    </div>
                    <Select value={rule.groupId} onValueChange={(v) => updateRule(i, { groupId: v })} disabled={isReadOnly}>
                      <SelectTrigger className="flex-1 h-8 text-xs"><SelectValue placeholder="Select group" /></SelectTrigger>
                      <SelectContent>
                        {groups.map((g) => {
                          const usedByOtherRule = rules.some((r, idx) => idx !== i && r.groupId === g.id);
                          return (
                            <SelectItem key={g.id} value={g.id} disabled={usedByOtherRule}>
                              {g.name} ({g.members.length} member{g.members.length !== 1 ? "s" : ""})
                              {usedByOtherRule ? " — already used" : ""}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">min</span>
                      <Select value={String(rule.minApprovals)} onValueChange={(v) => updateRule(i, { minApprovals: Number(v) })} disabled={isReadOnly}>
                        <SelectTrigger className="w-16 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {minApprovalOptions(groups, rule.groupId).map((n) => (
                            <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="text-xs text-muted-foreground">approval{rule.minApprovals !== 1 ? "s" : ""}</span>
                    </div>
                    {rules.length > 1 && !isReadOnly && (
                      <Button size="icon" variant="ghost" onClick={() => removeRule(i)}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addRule}
                  className="mt-1"
                  disabled={!canAddRule || isReadOnly}
                  title={!canAddRule ? "All groups are already used in this policy" : undefined}
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />Add Rule
                </Button>
                {rules.length > 0 && (
                  <div className="mt-3 p-3 bg-muted/40 rounded-md border border-border">
                    <p className="text-xs font-semibold text-foreground mb-1">Access Summary</p>
                    <p className="text-xs text-muted-foreground">
                      Access requires{" "}
                      {rules.map((r, i) => {
                        const g = groups.find((x) => x.id === r.groupId);
                        return (
                          <span key={i}>
                            {i > 0 && <span className="font-semibold text-foreground"> AND </span>}
                            <span className="font-medium text-foreground">{r.minApprovals} from {g?.name ?? "—"}</span>
                          </span>
                        );
                      })}.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={!valid}>
            {isEdit ? "Save Changes" : "Create Policy"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PolicyLinksBadges({
  usedInPolicies,
}: {
  usedInPolicies: { policy: ApprovalPolicy; minApprovals: number }[];
}) {
  const visible = usedInPolicies.slice(0, 2);
  const hidden = usedInPolicies.slice(2);

  const renderBadge = ({ policy, minApprovals }: { policy: ApprovalPolicy; minApprovals: number }) => (
    <Badge
      key={policy.id}
      variant="outline"
      className={`text-xs ${policy.status === "active"
        ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800"
        : "bg-slate-50 dark:bg-slate-950/40 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700"}`}
      title={`${policy.name} — min ${minApprovals} approval${minApprovals !== 1 ? "s" : ""}`}
    >
      {policy.status === "active"
        ? <CheckCircle2 className="w-3 h-3 mr-1" />
        : <XCircle className="w-3 h-3 mr-1" />}
      {policy.name}
      <span className="ml-1 opacity-70">· min {minApprovals}</span>
    </Badge>
  );

  return (
    <div className="flex items-center gap-1.5 flex-wrap min-h-[22px]">
      {visible.map(renderBadge)}
      {hidden.length > 0 && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-primary hover:text-primary/80 transition-colors cursor-pointer inline-flex items-center"
                onClick={(e) => e.stopPropagation()}
              >
                <Badge variant="outline" className="text-xs cursor-pointer">
                  +{hidden.length} more
                </Badge>
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-sm p-3">
              <div className="text-sm">
                <div className="mb-2 font-semibold text-foreground">
                  All Policies ({usedInPolicies.length})
                </div>
                <div className="space-y-1">
                  {usedInPolicies.map(({ policy, minApprovals }) => (
                    <div key={policy.id} className="text-muted-foreground">
                      • {policy.name} · min {minApprovals} · {policy.status}
                    </div>
                  ))}
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

// ─── Approval Groups Tab ──────────────────────────────────────────────────────

function GroupsTab({
  groups, total, page, searchInput, isRefreshing, policies, policiesLoading,
  onSearchInputChange, onSearchSubmit, onPageChange, onEdit, onDelete, onCancelActiveRequests, onCreate,
}: {
  groups: ApprovalGroup[];
  total: number;
  page: number;
  searchInput: string;
  isRefreshing?: boolean;
  policies: ApprovalPolicy[];
  policiesLoading?: boolean;
  onSearchInputChange: (value: string) => void;
  onSearchSubmit: () => void;
  onPageChange: (page: number) => void;
  onEdit: (g: ApprovalGroup) => void;
  onDelete: (id: string) => void;
  onCancelActiveRequests: (group: ApprovalGroup) => void;
  onCreate: () => void;
}) {
  const [pendingDelete, setPendingDelete] = useState<ApprovalGroup | null>(null);
  const [pendingCancel, setPendingCancel] = useState<ApprovalGroup | null>(null);

  const policiesForGroup = (groupId: string) =>
    policies.flatMap((policy) => {
      const rule = policy.rules.find((r) => r.groupId === groupId);
      return rule ? [{ policy, minApprovals: rule.minApprovals }] : [];
    });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 max-w-sm min-w-[12rem]">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              className="pl-8 h-9"
              placeholder="Search groups…"
              value={searchInput}
              onChange={(e) => onSearchInputChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") onSearchSubmit(); }}
            />
          </div>
          <Button
            size="icon"
            variant="outline"
            className="h-9 w-9 flex-shrink-0"
            onClick={onSearchSubmit}
            disabled={isRefreshing}
          >
            {isRefreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          </Button>
        </div>
        <Button onClick={onCreate} className="shrink-0">
          <Plus className="w-4 h-4 mr-1.5" />New Group
        </Button>
      </div>

      {groups.length === 0 && !isRefreshing ? (
        <p className="text-sm text-muted-foreground py-8 text-center">No groups match your search.</p>
      ) : (
        <>
          <div className="relative">
            {isRefreshing && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/60">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            )}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {groups.map((group) => {
                const usedInPolicies = policiesForGroup(group.id);
                const isSystemDefault = group.systemDefault === true;
                const canEdit = group.editable !== false && !isSystemDefault;
                const canDelete = canEdit;
                const editBlockedReason = isSystemDefault
                  ? "System-managed group cannot be edited"
                  : group.hasActivePendingRequests
                    ? "Resolve active pending approval requests before editing this group"
                    : undefined;
                return (
                  <div key={group.id} className="border border-border rounded-lg bg-card p-4 flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-foreground truncate">{group.name}</p>
                          {isSystemDefault && (
                            <Badge variant="outline" className="text-[10px] py-0 font-semibold bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800">
                              System
                            </Badge>
                          )}
                          {group.hasActivePendingRequests && !isSystemDefault && (
                            <Badge variant="outline" className="text-[10px] py-0 font-semibold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800">
                              Active requests
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{group.description}</p>
                        {group.hasActivePendingRequests && !isSystemDefault && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2 h-7 text-xs"
                            onClick={() => setPendingCancel(group)}
                          >
                            Cancel all requests
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          size="icon"
                          variant="ghost"
                          disabled={!canEdit}
                          title={editBlockedReason}
                          onClick={() => canEdit && onEdit(group)}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        {!isSystemDefault && (
                          <Button
                            size="icon"
                            variant="ghost"
                            disabled={!canDelete}
                            title={editBlockedReason}
                            onClick={() => canDelete && setPendingDelete(group)}
                          >
                            <Trash2 className="w-3.5 h-3.5 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <MemberAvatars members={group.members} />
                      <span className="text-xs text-muted-foreground shrink-0">{group.members.length} member{group.members.length !== 1 ? "s" : ""}</span>
                    </div>
                    <div className="border-t border-border pt-2.5 space-y-1.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Policies
                      </p>
                      {policiesLoading ? (
                        <span className="text-xs text-muted-foreground italic">Loading policies…</span>
                      ) : usedInPolicies.length === 0 ? (
                        <span className="text-xs text-muted-foreground italic">Not used in any policy</span>
                      ) : (
                        <PolicyLinksBadges usedInPolicies={usedInPolicies} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <Pagination page={page} total={total} pageSize={LIST_PAGE_SIZE} onChange={onPageChange} />
        </>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Approval Group</AlertDialogTitle>
            <AlertDialogDescription>
              Deleting <span className="font-medium text-foreground">{pendingDelete?.name}</span> will remove it from all policies that reference it. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"
              onClick={() => { onDelete(pendingDelete!.id); setPendingDelete(null); }}
            >
              Delete Group
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingCancel} onOpenChange={(o) => { if (!o) setPendingCancel(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel all active requests</AlertDialogTitle>
            <AlertDialogDescription>
              This will decline all pending multi-approval requests linked to group{" "}
              <span className="font-medium text-foreground">{pendingCancel?.name}</span>.
              This action is recorded in the audit log and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep requests</AlertDialogCancel>
            <AlertDialogAction
              className="bg-amber-600 hover:bg-amber-700 dark:bg-amber-600 dark:hover:bg-amber-700"
              onClick={() => {
                if (pendingCancel) onCancelActiveRequests(pendingCancel);
                setPendingCancel(null);
              }}
            >
              Cancel all requests
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Approval Policies Tab ────────────────────────────────────────────────────

function PoliciesTab({
  policies, total, page, searchInput, statusFilter, isRefreshing, groups,
  onSearchInputChange, onSearchSubmit, onStatusChange, onPageChange, onEdit, onDelete, onCancelActiveRequests, onCreate,
}: {
  policies: ApprovalPolicy[];
  total: number;
  page: number;
  searchInput: string;
  statusFilter: "all" | PolicyStatus;
  isRefreshing?: boolean;
  groups: ApprovalGroup[];
  onSearchInputChange: (value: string) => void;
  onSearchSubmit: () => void;
  onStatusChange: (value: "all" | PolicyStatus) => void;
  onPageChange: (page: number) => void;
  onEdit: (p: ApprovalPolicy) => void;
  onDelete: (id: string) => void;
  onCancelActiveRequests: (policy: ApprovalPolicy) => void;
  onCreate: () => void;
}) {
  const [pendingDelete, setPendingDelete] = useState<ApprovalPolicy | null>(null);
  const [pendingCancel, setPendingCancel] = useState<ApprovalPolicy | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-1 max-w-sm min-w-[12rem]">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                className="pl-8 h-9"
                placeholder="Search policies…"
                value={searchInput}
                onChange={(e) => onSearchInputChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") onSearchSubmit(); }}
              />
            </div>
            <Button
              size="icon"
              variant="outline"
              className="h-9 w-9 flex-shrink-0"
              onClick={onSearchSubmit}
              disabled={isRefreshing}
            >
              {isRefreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            </Button>
          </div>
          <div className="flex items-center gap-1">
            {(["all", "active", "inactive"] as const).map((s) => (
              <Button key={s} size="sm" variant={statusFilter === s ? "default" : "ghost"} onClick={() => onStatusChange(s)} className="capitalize">
                {s}
              </Button>
            ))}
          </div>
        </div>
        <Button onClick={onCreate} className="shrink-0">
          <Plus className="w-4 h-4 mr-1.5" />New Policy
        </Button>
      </div>

      {policies.length === 0 && !isRefreshing ? (
        <p className="text-sm text-muted-foreground py-8 text-center">No policies match your filter.</p>
      ) : (
        <>
          <div className="relative">
            {isRefreshing && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/60">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            )}
            <div className="space-y-2">
              {policies.map((policy) => {
                const isExpanded = expandedId === policy.id;
                const policyResources = policy.resources;
                const canEdit = policy.editable !== false;
                const canDelete = canEdit;
                const editBlockedReason = policy.hasActivePendingRequests
                  ? "Resolve active pending approval requests before editing this policy"
                  : undefined;
                return (
                  <div key={policy.id} className="border border-border rounded-lg bg-card overflow-hidden">
                    <button
                      className="w-full text-left px-4 py-3 flex items-center gap-3 hover-elevate"
                      onClick={() => setExpandedId(isExpanded ? null : policy.id)}
                    >
                      <ChevronRight className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-foreground">{policy.name}</p>
                          <Badge variant="outline" className={policy.status === "active"
                            ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800 text-xs"
                            : "bg-slate-50 dark:bg-slate-950/40 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 text-xs"}>
                            {policy.status === "active" ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                            {policy.status}
                          </Badge>
                          {policy.hasActivePendingRequests && (
                            <Badge variant="outline" className="text-[10px] py-0 font-semibold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800">
                              Active requests
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">{policy.description}</p>
                        {policy.hasActivePendingRequests && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2 h-7 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPendingCancel(policy);
                            }}
                          >
                            Cancel all requests
                          </Button>
                        )}
                      </div>
                      <div className="hidden md:flex items-center gap-1.5 flex-wrap shrink-0 ml-auto">
                        {policy.rules.map((rule, i) => {
                          const g = groups.find((x) => x.id === rule.groupId);
                          if (!g) return null;
                          return (
                            <span key={i} className="flex items-center gap-1">
                              {i > 0 && <span className="text-xs text-muted-foreground font-semibold">+</span>}
                              <Badge variant="outline" className="text-xs">
                                {rule.minApprovals} {g.name}
                              </Badge>
                            </span>
                          );
                        })}
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap ml-3 shrink-0">
                        {policyResources.length} resource{policyResources.length !== 1 ? "s" : ""}
                      </span>
                      <div className="flex items-center gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="icon"
                          variant="ghost"
                          disabled={!canEdit}
                          title={editBlockedReason}
                          onClick={() => canEdit && onEdit(policy)}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          disabled={!canDelete}
                          title={editBlockedReason}
                          onClick={() => canDelete && setPendingDelete(policy)}
                        >
                          <Trash2 className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="border-t border-border px-4 py-4 bg-muted/20 grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                            Resources ({policyResources.length})
                          </p>
                          <ResourceListSections resources={policyResources} />
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Approval Rules</p>
                          <div className="space-y-2">
                            {policy.rules.map((rule, i) => {
                              const g = groups.find((x) => x.id === rule.groupId);
                              if (!g) return null;
                              return (
                                <div key={i} className="flex items-center gap-2.5">
                                  <span className="text-xs text-foreground">
                                    <span className="font-semibold">{rule.minApprovals}</span> approval{rule.minApprovals !== 1 ? "s" : ""} from{" "}
                                    <span className="font-semibold">{g.name}</span>
                                  </span>
                                  <MemberAvatars members={g.members} max={3} />
                                </div>
                              );
                            })}
                          </div>
                          <div className="mt-3 p-2.5 bg-background rounded-md border border-border">
                            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                              <Info className="w-3 h-3 shrink-0" />
                              All {policy.rules.length} rule{policy.rules.length !== 1 ? "s" : ""} must be satisfied before access is granted.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <Pagination page={page} total={total} pageSize={LIST_PAGE_SIZE} onChange={onPageChange} />
        </>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Approval Policy</AlertDialogTitle>
            <AlertDialogDescription>
              Deleting <span className="font-medium text-foreground">{pendingDelete?.name}</span> means resource requests that match this policy will fall back to single-approval. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"
              onClick={() => { onDelete(pendingDelete!.id); setPendingDelete(null); }}
            >
              Delete Policy
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingCancel} onOpenChange={(o) => { if (!o) setPendingCancel(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel all active requests</AlertDialogTitle>
            <AlertDialogDescription>
              This will decline all pending multi-approval requests linked to policy{" "}
              <span className="font-medium text-foreground">{pendingCancel?.name}</span>.
              This action is recorded in the audit log and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep requests</AlertDialogCancel>
            <AlertDialogAction
              className="bg-amber-600 hover:bg-amber-700 dark:bg-amber-600 dark:hover:bg-amber-700"
              onClick={() => {
                if (pendingCancel) onCancelActiveRequests(pendingCancel);
                setPendingCancel(null);
              }}
            >
              Cancel all requests
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function MultiApprovals() {
  const tenantName = useMemo(() => {
    const latestTenant = myLocalStorage.getItem("latestTenant");
    return latestTenant?.tenantName
      || (typeof latestTenant === "string" ? latestTenant : "")
      || "";
  }, []);
  const { toast } = useToast();
  const [groups, setGroups] = useState<ApprovalGroup[]>([]);
  const [groupsTotal, setGroupsTotal] = useState(0);
  const [groupsPage, setGroupsPage] = useState(1);
  const [groupsSearchInput, setGroupsSearchInput] = useState("");
  const [groupsSearchApplied, setGroupsSearchApplied] = useState("");
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsHasLoaded, setGroupsHasLoaded] = useState(false);
  const [selectorGroups, setSelectorGroups] = useState<ApprovalGroup[]>([]);

  const [policies, setPolicies] = useState<ApprovalPolicy[]>([]);
  const [policiesTotal, setPoliciesTotal] = useState(0);
  const [policiesPage, setPoliciesPage] = useState(1);
  const [policiesSearchInput, setPoliciesSearchInput] = useState("");
  const [policiesSearchApplied, setPoliciesSearchApplied] = useState("");
  const [policiesStatusFilter, setPoliciesStatusFilter] = useState<"all" | PolicyStatus>("all");
  const [policiesLoading, setPoliciesLoading] = useState(false);
  const [policiesHasLoaded, setPoliciesHasLoaded] = useState(false);
  const [allPolicies, setAllPolicies] = useState<ApprovalPolicy[]>([]);
  const [allPoliciesLoading, setAllPoliciesLoading] = useState(false);

  const handleGroupsSearchInputChange = (value: string) => {
    setGroupsSearchInput(value);
    if (!value.trim() && groupsSearchApplied) {
      setGroupsPage(1);
      setGroupsSearchApplied("");
    }
  };

  const handlePoliciesSearchInputChange = (value: string) => {
    setPoliciesSearchInput(value);
    if (!value.trim() && policiesSearchApplied) {
      setPoliciesPage(1);
      setPoliciesSearchApplied("");
    }
  };

  const knownPolicies = useMemo(() => {
    const byId = new Map<string, ApprovalPolicy>();
    for (const policy of [...allPolicies, ...policies]) {
      if (policy.id) {
        byId.set(policy.id, policy);
      }
    }
    return Array.from(byId.values());
  }, [allPolicies, policies]);

  const [groupDialog, setGroupDialog] = useState<{ open: boolean; initial: Partial<ApprovalGroup> | null }>({ open: false, initial: null });
  const [policyDialog, setPolicyDialog] = useState<{ open: boolean; initial: Partial<ApprovalPolicy> | null }>({ open: false, initial: null });

  const loadSelectorGroups = useCallback(async () => {
    if (!tenantName) return;
    try {
      const response = await fetchMultiApprovalGroups(tenantName, 0, SELECTOR_FETCH_SIZE);
      setSelectorGroups(response.content.map(apiGroupToApprovalGroup));
    } catch (error) {
      console.error("Failed to load approval groups for selectors:", error);
    }
  }, [tenantName]);

  const loadAllPolicies = useCallback(async () => {
    if (!tenantName) return;
    setAllPoliciesLoading(true);
    try {
      const response = await fetchMultiApprovalPolicies(tenantName, 0, SELECTOR_FETCH_SIZE);
      setAllPolicies(response.content.map(apiPolicyToApprovalPolicy));
    } catch (error) {
      console.error("Failed to load policy summaries:", error);
    } finally {
      setAllPoliciesLoading(false);
    }
  }, [tenantName]);

  const loadGroups = useCallback(async () => {
    if (!tenantName) return;
    setGroupsLoading(true);
    try {
      const response = await fetchMultiApprovalGroups(tenantName, groupsPage - 1, LIST_PAGE_SIZE, groupsSearchApplied);
      setGroups(response.content.map(apiGroupToApprovalGroup));
      setGroupsTotal(response.totalElements);
    } catch (error) {
      console.error("Failed to load approval groups:", error);
      toast({
        title: "Failed to load groups",
        description: "Could not fetch approval groups. Please try again.",
        variant: "destructive",
      });
    } finally {
      setGroupsLoading(false);
      setGroupsHasLoaded(true);
    }
  }, [tenantName, toast, groupsPage, groupsSearchApplied]);

  const loadPolicies = useCallback(async () => {
    if (!tenantName) return;
    setPoliciesLoading(true);
    try {
      const response = await fetchMultiApprovalPolicies(
        tenantName,
        policiesPage - 1,
        LIST_PAGE_SIZE,
        policiesSearchApplied,
        policiesStatusFilter,
      );
      setPolicies(response.content.map(apiPolicyToApprovalPolicy));
      setPoliciesTotal(response.totalElements);
    } catch (error) {
      console.error("Failed to load approval policies:", error);
      toast({
        title: "Failed to load policies",
        description: "Could not fetch approval policies. Please try again.",
        variant: "destructive",
      });
    } finally {
      setPoliciesLoading(false);
      setPoliciesHasLoaded(true);
    }
  }, [tenantName, toast, policiesPage, policiesSearchApplied, policiesStatusFilter]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  useEffect(() => {
    loadSelectorGroups();
    loadAllPolicies();
  }, [loadSelectorGroups, loadAllPolicies]);

  useEffect(() => {
    if (policyDialog.open) {
      void loadAllPolicies();
    }
  }, [policyDialog.open, loadAllPolicies]);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadGroups(), loadPolicies(), loadSelectorGroups(), loadAllPolicies()]);
  }, [loadGroups, loadPolicies, loadSelectorGroups, loadAllPolicies]);

  const buildMemberPayload = (members: GroupMember[]) =>
    members.map((member) => ({
      tenantAdminId: Number(member.id),
      email: member.email,
      roleName: member.role,
      firstName: member.firstName ?? member.name.split(" ")[0] ?? "",
      lastName: member.lastName ?? member.name.split(" ").slice(1).join(" ") ?? "",
    }));

  const saveGroup = async (data: Omit<ApprovalGroup, "id"> & { id?: string }) => {
    if (!tenantName) return;
    try {
      const payload = {
        name: data.name,
        description: data.description,
        tenantName,
        members: buildMemberPayload(data.members),
      };

      if (data.id) {
        await updateMultiApprovalGroup({ ...payload, uuid: data.id });
        toast({ title: "Group updated", description: `"${data.name}" has been saved.` });
      } else {
        await createMultiApprovalGroup(payload);
        toast({ title: "Group created", description: `"${data.name}" has been created.` });
      }
      await refreshAll();
    } catch (error: unknown) {
      console.error("Failed to save approval group:", error);
      const description =
        (error as {
          response?: { data?: { message?: string } };
          message?: string;
        })?.response?.data?.message ??
        (error as { message?: string })?.message ??
        "Could not save the approval group. Please try again.";
      toast({
        title: "Save failed",
        description,
        variant: "destructive",
      });
    }
  };

  const deleteGroup = async (id: string) => {
    if (!tenantName) return;
    try {
      await deleteMultiApprovalGroup(id, tenantName);
      await refreshAll();
      toast({ title: "Group deleted", description: "The approval group has been removed." });
    } catch (error) {
      console.error("Failed to delete approval group:", error);
      toast({
        title: "Delete failed",
        description: "Could not delete the approval group. Please try again.",
        variant: "destructive",
      });
    }
  };

  const savePolicy = async (data: Omit<ApprovalPolicy, "id" | "createdAt"> & { id?: string }) => {
    if (!tenantName) return;
    try {
      const payload = {
        name: data.name,
        description: data.description,
        status: data.status,
        tenantName,
        resources: data.resources.map((resource) => ({
          resourceSource: resource.resourceSource,
          resourceType: resource.resourceType,
          resourceId: resource.resourceId,
          resourceName: resource.name,
          resourceKey: resource.resourceKey,
          resourceSourceLabel: resource.resourceSourceLabel,
          resourceTypeLabel: resource.resourceTypeLabel,
        })),
        rules: data.rules.map((rule, index) => ({
          groupUuid: rule.groupId,
          minApprovals: rule.minApprovals,
          ruleOrder: index,
        })),
      };

      if (data.id) {
        await updateMultiApprovalPolicy({ ...payload, uuid: data.id });
        toast({ title: "Policy updated", description: `"${data.name}" has been saved.` });
      } else {
        await createMultiApprovalPolicy(payload);
        toast({ title: "Policy created", description: `"${data.name}" has been created.` });
      }
      await refreshAll();
    } catch (error: unknown) {
      console.error("Failed to save approval policy:", error);
      const description =
        (error as {
          response?: { data?: { message?: string } };
          message?: string;
        })?.response?.data?.message ??
        (error as { message?: string })?.message ??
        "Could not save the approval policy. Please try again.";
      toast({
        title: "Save failed",
        description,
        variant: "destructive",
      });
    }
  };

  const deletePolicy = async (id: string) => {
    if (!tenantName) return;
    try {
      await deleteMultiApprovalPolicy(id, tenantName);
      await refreshAll();
      toast({ title: "Policy deleted", description: "The approval policy has been removed." });
    } catch (error) {
      console.error("Failed to delete approval policy:", error);
      toast({
        title: "Delete failed",
        description: "Could not delete the approval policy. Please try again.",
        variant: "destructive",
      });
    }
  };

  const cancelGroupRequests = async (group: ApprovalGroup) => {
    if (!tenantName) return;
    try {
      const cancelled = await cancelActiveRequestsForGroup(group.id, tenantName);
      await refreshAll();
      toast({
        title: "Requests cancelled",
        description: cancelled > 0
          ? `Declined ${cancelled} active request${cancelled !== 1 ? "s" : ""} for "${group.name}".`
          : `No active pending requests remained for "${group.name}".`,
      });
    } catch (error) {
      console.error("Failed to cancel group requests:", error);
      toast({
        title: "Cancel failed",
        description: "Could not cancel active requests for this group.",
        variant: "destructive",
      });
    }
  };

  const cancelPolicyRequests = async (policy: ApprovalPolicy) => {
    if (!tenantName) return;
    try {
      const cancelled = await cancelActiveRequestsForPolicy(policy.id, tenantName);
      await refreshAll();
      toast({
        title: "Requests cancelled",
        description: cancelled > 0
          ? `Declined ${cancelled} active request${cancelled !== 1 ? "s" : ""} for "${policy.name}".`
          : `No active pending requests remained for "${policy.name}".`,
      });
    } catch (error) {
      console.error("Failed to cancel policy requests:", error);
      toast({
        title: "Cancel failed",
        description: "Could not cancel active requests for this policy.",
        variant: "destructive",
      });
    }
  };

  const activeCount = allPolicies.filter((p) => p.status === "active").length;

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full overflow-hidden bg-white dark:bg-background">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between gap-4 flex-wrap bg-white dark:bg-background">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
              <Layers className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-foreground">Multi-Approval Policies</h1>
              <p className="text-xs text-muted-foreground">Require sign-off from multiple teams before granting resource access</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 border border-border rounded-md px-3 py-1.5">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>{groupsTotal} groups · {activeCount} active policies</span>
          </div>
        </div>

        <Tabs defaultValue="policies" className="flex flex-col flex-1 overflow-hidden">
          <div className="border-b border-border px-6 flex items-center justify-between gap-4">
            <TabsList className="h-10 bg-transparent p-0 gap-0">
              <TabsTrigger value="policies" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none h-10 px-4 text-sm">
                Approval Policies
              </TabsTrigger>
              <TabsTrigger value="groups" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none h-10 px-4 text-sm">
                Approval Groups
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="policies" className="flex-1 overflow-y-auto mt-0 data-[state=inactive]:hidden">
            {(!policiesHasLoaded && policiesLoading) ? (
              <div className="flex items-center justify-center py-16 text-sm text-muted-foreground gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading policies…
              </div>
            ) : (
              <PoliciesTab
                policies={policies}
                total={policiesTotal}
                page={policiesPage}
                searchInput={policiesSearchInput}
                isRefreshing={policiesLoading}
                statusFilter={policiesStatusFilter}
                groups={selectorGroups}
                onSearchInputChange={handlePoliciesSearchInputChange}
                onSearchSubmit={() => {
                  const trimmed = policiesSearchInput.trim();
                  setPoliciesPage(1);
                  if (trimmed === policiesSearchApplied) {
                    void loadPolicies();
                  } else {
                    setPoliciesSearchApplied(trimmed);
                  }
                }}
                onStatusChange={(value) => { setPoliciesStatusFilter(value); setPoliciesPage(1); }}
                onPageChange={setPoliciesPage}
                onEdit={(p) => setPolicyDialog({ open: true, initial: p })}
                onDelete={deletePolicy}
                onCancelActiveRequests={cancelPolicyRequests}
                onCreate={() => setPolicyDialog({ open: true, initial: null })}
              />
            )}
          </TabsContent>

          <TabsContent value="groups" className="flex-1 overflow-y-auto mt-0 data-[state=inactive]:hidden">
            {(!groupsHasLoaded && groupsLoading) ? (
              <div className="flex items-center justify-center py-16 text-sm text-muted-foreground gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading groups…
              </div>
            ) : (
              <GroupsTab
                groups={groups}
                total={groupsTotal}
                page={groupsPage}
                searchInput={groupsSearchInput}
                isRefreshing={groupsLoading}
                policies={allPolicies}
                policiesLoading={allPoliciesLoading}
                onSearchInputChange={handleGroupsSearchInputChange}
                onSearchSubmit={() => {
                  const trimmed = groupsSearchInput.trim();
                  setGroupsPage(1);
                  if (trimmed === groupsSearchApplied) {
                    void loadGroups();
                  } else {
                    setGroupsSearchApplied(trimmed);
                  }
                }}
                onPageChange={setGroupsPage}
                onEdit={(g) => setGroupDialog({ open: true, initial: g })}
                onDelete={deleteGroup}
                onCancelActiveRequests={cancelGroupRequests}
                onCreate={() => setGroupDialog({ open: true, initial: null })}
              />
            )}
          </TabsContent>
        </Tabs>

        <GroupDialog
          open={groupDialog.open}
          onOpenChange={(v) => setGroupDialog((s) => ({ ...s, open: v }))}
          initial={groupDialog.initial}
          onSave={saveGroup}
          tenantName={tenantName}
        />
        <PolicyDialog
          open={policyDialog.open}
          onOpenChange={(v) => setPolicyDialog((s) => ({ ...s, open: v }))}
          initial={policyDialog.initial}
          groups={selectorGroups}
          allPolicies={knownPolicies}
          onSave={savePolicy}
          tenantName={tenantName}
        />
      </div>
    </TooltipProvider>
  );
}
